class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);
    this.onReposition = this.positionNotification.bind(this);
    this.onTransitionEnd = this.handleTransitionEnd.bind(this);
    this.bodyClickTimeout = null;
    this.shouldTrapFocusOnEnter = false;

    if (!this.notification) return;

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.notification.addEventListener('transitionend', this.onTransitionEnd);
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
  }

  handleTransitionEnd(event) {
    if (event.target !== this.notification || event.propertyName !== 'transform') return;

    if (this.notification.classList.contains('active')) {
      if (this.shouldTrapFocusOnEnter) {
        this.shouldTrapFocusOnEnter = false;
        this.notification.focus();
        trapFocus(this.notification);
      }
      return;
    }

    if (this.notification.classList.contains('animate')) {
      this.notification.classList.remove('animate');
    }
  }

  open() {
    if (!this.notification) return;

    if (this.bodyClickTimeout) {
      clearTimeout(this.bodyClickTimeout);
      this.bodyClickTimeout = null;
    }
    document.body.removeEventListener('click', this.onBodyClick);

    this.positionNotification();
    this.notification.removeAttribute('hidden');
    this.notification.classList.add('animate');
    this.notification.classList.remove('active');
    this.shouldTrapFocusOnEnter = true;

    window.addEventListener('resize', this.onReposition);
    window.addEventListener('scroll', this.onReposition, { passive: true });

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.notification.classList.add('active');
      });
    });

    // カート投入クリックやアプリUIのイベントが body に届いて即閉じるのを防ぐ
    this.bodyClickTimeout = setTimeout(() => {
      this.bodyClickTimeout = null;
      document.body.addEventListener('click', this.onBodyClick);
    }, 500);
  }

  close() {
    if (!this.notification) return;

    if (this.bodyClickTimeout) {
      clearTimeout(this.bodyClickTimeout);
      this.bodyClickTimeout = null;
    }

    this.notification.classList.remove('active');
    this.shouldTrapFocusOnEnter = false;

    document.body.removeEventListener('click', this.onBodyClick);
    window.removeEventListener('resize', this.onReposition);
    window.removeEventListener('scroll', this.onReposition);

    removeTrapFocus(this.activeElement);
  }

  positionNotification() {
    const cartIcon = document.getElementById('cart-icon-bubble');
    if (!cartIcon || !this.notification) return;

    const iconRect = cartIcon.getBoundingClientRect();
    const maxWidth = Math.min(368, window.innerWidth - 24);
    let right = window.innerWidth - iconRect.right;

    if (window.innerWidth - right - maxWidth < 12) {
      right = Math.max(12, window.innerWidth - maxWidth - 12);
    }

    this.notification.style.position = 'fixed';
    this.notification.style.top = `${Math.round(iconRect.bottom)}px`;
    this.notification.style.right = `${Math.round(right)}px`;
    this.notification.style.left = 'auto';
    this.notification.style.width = `${maxWidth}px`;
    this.notification.style.maxWidth = `${maxWidth}px`;
  }

  renderContents(parsedState) {
    if (!parsedState || !this.notification) return;

    this.cartItemKey = parsedState.key;

    this.updateSections(parsedState)
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        if (this.header?.reveal) this.header.reveal();
        requestAnimationFrame(() => {
          requestAnimationFrame(() => this.open());
        });
      });
  }

  async updateSections(parsedState) {
    let sections = parsedState.sections;

    if (!sections?.['cart-notification-product']) {
      sections = await this.fetchSectionHtml(parsedState);
    }

    this.getSectionsToRender().forEach((section) => {
      const target = document.getElementById(section.id);
      const sectionHtml = sections?.[section.id];
      if (!target || !sectionHtml) return;

      target.innerHTML = this.getSectionInnerHTML(sectionHtml, section.selector);
    });

    const productTarget = document.getElementById('cart-notification-product');
    if (productTarget && !productTarget.innerHTML.trim()) {
      productTarget.innerHTML = this.buildFallbackProductMarkup(parsedState);
    }
  }

  async fetchSectionHtml(parsedState) {
    const sectionIds = this.getSectionsToRender()
      .map((section) => section.id)
      .join(',');

    try {
      const response = await fetch(`${routes.cart_url}?sections=${sectionIds}`);
      if (!response.ok) return parsedState.sections || {};

      const fetchedSections = await response.json();
      return { ...(parsedState.sections || {}), ...fetchedSections };
    } catch (error) {
      console.error(error);
      return parsedState.sections || {};
    }
  }

  buildFallbackProductMarkup(parsedState) {
    const title = parsedState.product_title || parsedState.title || '';
    const image = parsedState.featured_image?.url || parsedState.image;
    let markup = '<div class="cart-item">';

    if (image) {
      markup += `
        <div class="cart-notification-product__image global-media-settings">
          <img src="${image}" alt="" width="70" height="70" loading="lazy">
        </div>
      `;
    }

    markup += `
      <div>
        <h3 class="cart-notification-product__name h4">${this.escapeHtml(title)}</h3>
      </div>
    </div>`;

    return markup;
  }

  escapeHtml(text) {
    const element = document.createElement('div');
    element.textContent = text;
    return element.innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return '';

    const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
    const element = parsedDocument.querySelector(selector);
    if (element) return element.innerHTML;

    if (selector?.includes('cart-notification-product-')) {
      const cartItem = parsedDocument.querySelector('.cart-item');
      if (cartItem) return cartItem.innerHTML;
    }

    const sectionFallback = parsedDocument.querySelector('.shopify-section');
    return sectionFallback ? sectionFallback.innerHTML : html;
  }

  handleBodyClick(evt) {
    const target = evt.target;

    if (
      target.closest(
        'product-form, .product-form__submit, .shopify-payment-button, shopify-accelerated-checkout, quick-add-modal, cart-notification'
      )
    ) {
      return;
    }

    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
